/**
 * PDF Cache Service using IndexedDB
 *
 * Caches PDF blobs for as-built drawings to improve performance
 * when switching between sites. PDFs are static content, so they
 * can be cached indefinitely.
 */

const DB_NAME = 'engramiq-pdf-cache';
const DB_VERSION = 1;
const STORE_NAME = 'pdf-blobs';

interface CachedPDF {
  /** Unique key: artefact ID */
  id: string;
  /** The PDF blob data */
  blob: Blob;
  /** Timestamp when cached */
  cachedAt: number;
  /** Site ID for reference */
  siteId: string;
  /** Tenant ID for reference */
  tenantId: string;
  /** File size in bytes */
  size: number;
}

// Maximum cache size (500MB default)
const MAX_CACHE_SIZE_BYTES = 500 * 1024 * 1024;

// Maximum age for cached PDFs (30 days) - optional cleanup
const MAX_CACHE_AGE_MS = 30 * 24 * 60 * 60 * 1000;

let dbInstance: IDBDatabase | null = null;
let dbInitPromise: Promise<IDBDatabase> | null = null;

/**
 * Initialize the IndexedDB database
 */
function initDB(): Promise<IDBDatabase> {
  if (dbInstance) {
    return Promise.resolve(dbInstance);
  }

  if (dbInitPromise) {
    return dbInitPromise;
  }

  dbInitPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      dbInitPromise = null;
      reject(request.error);
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create the object store for PDF blobs
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('siteId', 'siteId', { unique: false });
        store.createIndex('tenantId', 'tenantId', { unique: false });
        store.createIndex('cachedAt', 'cachedAt', { unique: false });
      }
    };
  });

  return dbInitPromise;
}

/**
 * Get a cached PDF by artefact ID
 */
export async function getCachedPDF(artefactId: string): Promise<Blob | null> {
  try {
    const db = await initDB();

    return new Promise((resolve) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(artefactId);

      request.onerror = () => {
        resolve(null);
      };

      request.onsuccess = () => {
        const result = request.result as CachedPDF | undefined;
        resolve(result?.blob ?? null);
      };
    });
  } catch {
    return null;
  }
}

/**
 * Cache a PDF blob
 */
export async function cachePDF(
  artefactId: string,
  blob: Blob,
  siteId: string,
  tenantId: string
): Promise<boolean> {
  try {
    const db = await initDB();

    // Check and potentially clean up cache if it's getting too large
    await cleanupCacheIfNeeded(db);

    const cacheEntry: CachedPDF = {
      id: artefactId,
      blob,
      cachedAt: Date.now(),
      siteId,
      tenantId,
      size: blob.size,
    };

    return new Promise((resolve) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(cacheEntry);

      request.onerror = () => {
        resolve(false);
      };

      request.onsuccess = () => {
        resolve(true);
      };
    });
  } catch {
    return false;
  }
}

/**
 * Remove a specific PDF from cache
 */
export async function removeCachedPDF(artefactId: string): Promise<boolean> {
  try {
    const db = await initDB();

    return new Promise((resolve) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(artefactId);

      request.onerror = () => {
        resolve(false);
      };

      request.onsuccess = () => {
        resolve(true);
      };
    });
  } catch {
    return false;
  }
}

/**
 * Clear all cached PDFs for a specific site
 */
export async function clearSiteCache(siteId: string): Promise<boolean> {
  try {
    const db = await initDB();

    return new Promise((resolve) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('siteId');
      const request = index.openCursor(IDBKeyRange.only(siteId));

      request.onerror = () => {
        resolve(false);
      };

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve(true);
        }
      };
    });
  } catch {
    return false;
  }
}

/**
 * Clear all cached PDFs
 */
export async function clearAllCache(): Promise<boolean> {
  try {
    const db = await initDB();

    return new Promise((resolve) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onerror = () => {
        resolve(false);
      };

      request.onsuccess = () => {
        resolve(true);
      };
    });
  } catch {
    return false;
  }
}

/**
 * Get cache statistics
 */
export async function getCacheStats(): Promise<{
  count: number;
  totalSize: number;
  entries: Array<{ id: string; siteId: string; size: number; cachedAt: number }>;
}> {
  try {
    const db = await initDB();

    return new Promise((resolve) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onerror = () => {
        resolve({ count: 0, totalSize: 0, entries: [] });
      };

      request.onsuccess = () => {
        const results = request.result as CachedPDF[];
        const totalSize = results.reduce((sum, entry) => sum + entry.size, 0);
        const entries = results.map(({ id, siteId, size, cachedAt }) => ({
          id,
          siteId,
          size,
          cachedAt,
        }));

        resolve({
          count: results.length,
          totalSize,
          entries,
        });
      };
    });
  } catch {
    return { count: 0, totalSize: 0, entries: [] };
  }
}

/**
 * Check if a PDF is cached
 */
export async function isPDFCached(artefactId: string): Promise<boolean> {
  try {
    const db = await initDB();

    return new Promise((resolve) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.count(IDBKeyRange.only(artefactId));

      request.onerror = () => {
        resolve(false);
      };

      request.onsuccess = () => {
        resolve(request.result > 0);
      };
    });
  } catch {
    return false;
  }
}

/**
 * Clean up cache if it exceeds the size limit
 * Removes oldest entries first
 */
async function cleanupCacheIfNeeded(db: IDBDatabase): Promise<void> {
  return new Promise((resolve) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onerror = () => {
      resolve();
    };

    request.onsuccess = () => {
      const results = request.result as CachedPDF[];
      const totalSize = results.reduce((sum, entry) => sum + entry.size, 0);
      const now = Date.now();

      // Remove expired entries
      const expiredEntries = results.filter(
        (entry) => now - entry.cachedAt > MAX_CACHE_AGE_MS
      );

      for (const entry of expiredEntries) {
        store.delete(entry.id);
      }

      // If still over limit, remove oldest entries
      if (totalSize > MAX_CACHE_SIZE_BYTES) {
        const sortedByAge = results
          .filter((entry) => !expiredEntries.includes(entry))
          .sort((a, b) => a.cachedAt - b.cachedAt);

        let currentSize = totalSize;
        for (const entry of sortedByAge) {
          if (currentSize <= MAX_CACHE_SIZE_BYTES * 0.8) {
            break;
          }
          store.delete(entry.id);
          currentSize -= entry.size;
        }
      }

      resolve();
    };
  });
}

/**
 * Format bytes to human readable string
 */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Export format bytes utility for use in components
 */
export { formatBytes };
